import { useEffect, useMemo, useRef, useState } from 'react';

import { hitlService } from '../services/hitl';
import type { HitlActionRequest, HitlItem } from '../services/types';
import { useAppStore } from '../store/appStore';

import { AuditTrail } from './AuditTrail';
import { Button } from './Button';
import { PII_OPTION_LABELS } from './HitlQueue';
import { Spinner } from './Spinner';

export interface ReviewerModalProps {
  hit: HitlItem | null;
  isOpen: boolean;
  onClose: () => void;
  /** Called after any action succeeds, so the parent can refresh its cache. */
  onActionComplete?: (hit: HitlItem) => void;
  reviewerId: string;
}

/**
 * Full reviewer modal for a single HITL item.
 *
 * Layout
 * ------
 *   ┌─ header (severity / column / agent / hit_id / close ─┐
 *   │  issue paragraph                                      │
 *   │  sample rows (original vs cleaned)                    │
 *   │  distribution bar (original-value counts)             │
 *   │  action panel: radio options + comment + buttons      │
 *   │  audit trail                                          │
 *   └───────────────────────────────────────────────────────┘
 *
 * Focus + keyboard
 * ----------------
 * - Opens with focus on the Close button (safe default, avoids accidental
 *   action confirmation if the reviewer hits Enter).
 * - Escape closes.
 * - A simple focus trap keeps tab inside the dialog.
 * - `role="dialog"` + `aria-modal` + `aria-labelledby` for screen readers.
 */
export function ReviewerModal({
  hit,
  isOpen,
  onClose,
  onActionComplete,
  reviewerId,
}: ReviewerModalProps) {
  const setAuditTrail = useAppStore((s) => s.setAuditTrail);
  const appendAudit = useAppStore((s) => s.appendAudit);
  const updateHit = useAppStore((s) => s.updateHit);
  const cachedAudit = useAppStore((s) => (hit ? s.auditByHit[hit.hit_id] : undefined));

  const [auditLoading, setAuditLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [comment, setComment] = useState('');
  const [pending, setPending] = useState<HitlActionRequest['type'] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Fetch the audit trail whenever a hit is opened. Cached so subsequent
  // re-opens are instant.
  useEffect(() => {
    if (!isOpen || !hit) return;
    let cancelled = false;
    setAuditLoading(true);
    hitlService
      .getAudit(hit.hit_id)
      .then((entries) => {
        if (!cancelled) setAuditTrail(hit.hit_id, entries);
      })
      .catch(() => {
        // Swallow — the timeline stays on whatever the store already has.
      })
      .finally(() => {
        if (!cancelled) setAuditLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, hit, setAuditTrail]);

  // Reset transient UI bits when the modal opens onto a new hit.
  useEffect(() => {
    if (isOpen && hit) {
      setSelectedOption(hit.options?.[0] ?? '');
      setComment('');
      setError(null);
      setPending(null);
      // Defer focus to next tick so the dialog element is actually mounted.
      requestAnimationFrame(() => closeBtnRef.current?.focus());
    }
  }, [isOpen, hit]);

  // Escape-to-close + basic tab trap.
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusables = dialogRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && document.activeElement === last) {
        first.focus();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const distribution = useMemo(() => computeDistribution(hit), [hit]);

  if (!isOpen || !hit) return null;

  async function dispatch(req: HitlActionRequest) {
    if (!hit) return;
    setPending(req.type);
    setError(null);
    const previousStatus = hit.status;
    const projected = hitlService.resolvedStatus(req.type);
    // Optimistic: flip the cached hit status immediately.
    updateHit(hit.hit_id, { status: projected });
    try {
      const result = await hitlService.act(hit.hit_id, req);
      appendAudit(hit.hit_id, result.audit);
      onActionComplete?.({ ...hit, status: projected });
    } catch (err) {
      // Roll back.
      updateHit(hit.hit_id, { status: previousStatus });
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setPending(null);
    }
  }

  const disabled = pending !== null || hit.status !== 'open';

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/40 px-4 py-8 overflow-y-auto"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="reviewer-modal-title"
        className="w-full max-w-3xl rounded-lg bg-paper shadow-card"
      >
        <header className="flex items-start justify-between gap-4 border-b border-border px-6 py-4">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span
              className={`inline-block rounded-sm px-2 py-0.5 text-xs font-semibold uppercase ${severityStyles[hit.severity]}`}
            >
              {hit.severity}
            </span>
            <h2 id="reviewer-modal-title" className="text-lg font-semibold truncate">
              <code>{hit.column}</code>
              <span className="mx-2 text-muted">·</span>
              <span className="text-muted">{hit.agent_id}</span>
            </h2>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <code className="text-xs text-muted hidden sm:inline">{hit.hit_id}</code>
            <button
              ref={closeBtnRef}
              type="button"
              aria-label="Close reviewer"
              onClick={onClose}
              className="rounded px-2 py-1 text-muted hover:bg-surface-alt"
            >
              ✕
            </button>
          </div>
        </header>

        <div className="px-6 py-5 space-y-5">
          <p className="text-sm">{hit.issue}</p>

          {hit.sample_rows.length > 0 && (
            <section aria-labelledby="rm-samples">
              <h3 id="rm-samples" className="text-xs uppercase tracking-wide text-muted mb-2">
                Sample rows
              </h3>
              <div className="rounded border border-border overflow-hidden">
                <table className="min-w-full text-xs">
                  <thead className="bg-surface-alt/60">
                    <tr>
                      <th className="text-left px-3 py-1.5 font-medium text-muted">row</th>
                      <th className="text-left px-3 py-1.5 font-medium text-muted">original</th>
                      <th className="text-left px-3 py-1.5 font-medium text-muted">cleaned</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hit.sample_rows.map((r, i) => (
                      <tr key={i} className="odd:bg-surface-alt/20">
                        <td className="px-3 py-1.5 font-mono text-muted">{String(r.row_id)}</td>
                        <td className="px-3 py-1.5 font-mono">{formatCell(r.original)}</td>
                        <td className="px-3 py-1.5 font-mono">{formatCell(r.cleaned)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {distribution.length > 0 && (
            <section aria-labelledby="rm-dist">
              <h3 id="rm-dist" className="text-xs uppercase tracking-wide text-muted mb-2">
                Original-value distribution
              </h3>
              <ul className="space-y-1 text-xs">
                {distribution.map((d) => (
                  <li key={d.label} className="flex items-center gap-2">
                    <span className="font-mono w-32 truncate" title={d.label}>
                      {d.label}
                    </span>
                    <div className="flex-1 bg-surface-alt/40 rounded-sm h-3 overflow-hidden">
                      <div
                        className="h-full bg-brand/70"
                        style={{ width: `${d.pct}%` }}
                        aria-hidden
                      />
                    </div>
                    <span className="w-10 text-right text-muted">{d.count}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section aria-labelledby="rm-action" className="space-y-3">
            <h3 id="rm-action" className="text-xs uppercase tracking-wide text-muted">
              Action
            </h3>

            {hit.options && hit.options.length > 0 && (
              <fieldset className="space-y-1" disabled={disabled}>
                <legend className="sr-only">Choose option</legend>
                {hit.options.map((opt) => (
                  <label key={opt} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="hit-option"
                      value={opt}
                      checked={selectedOption === opt}
                      onChange={() => setSelectedOption(opt)}
                    />
                    <span>{PII_OPTION_LABELS[opt] ?? opt}</span>
                  </label>
                ))}
              </fieldset>
            )}

            <label className="block text-sm">
              <span className="text-xs uppercase tracking-wide text-muted">Comment (optional)</span>
              <textarea
                className="mt-1 w-full rounded border border-border bg-paper px-3 py-2 text-sm"
                rows={2}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={disabled}
                placeholder="Context for teammates or future you…"
              />
            </label>

            <div className="flex flex-wrap items-center gap-2">
              {hit.options && hit.options.length > 0 && (
                <Button
                  variant="primary"
                  disabled={disabled || !selectedOption}
                  onClick={() =>
                    dispatch({
                      type: 'choose_format',
                      payload: { choice: selectedOption },
                      reviewer_id: reviewerId,
                      comment: comment || undefined,
                    })
                  }
                >
                  {pending === 'choose_format' ? <Spinner /> : null}
                  Apply to chunk
                </Button>
              )}
              <Button
                variant="secondary"
                disabled={disabled}
                onClick={() =>
                  dispatch({
                    type: 'accept',
                    reviewer_id: reviewerId,
                    comment: comment || undefined,
                  })
                }
              >
                {pending === 'accept' ? <Spinner /> : null}
                Accept agent output
              </Button>
              <Button
                variant="ghost"
                disabled={disabled}
                onClick={() =>
                  dispatch({
                    type: 'escalate',
                    reviewer_id: reviewerId,
                    comment: comment || undefined,
                  })
                }
              >
                {pending === 'escalate' ? <Spinner /> : null}
                Escalate
              </Button>
              <span className="mx-2 text-muted">|</span>
              <Button
                variant="ghost"
                disabled={disabled || !hit.chunk_id}
                onClick={() =>
                  dispatch({
                    type: 'rerun_chunk',
                    payload: { chunk_id: hit.chunk_id },
                    reviewer_id: reviewerId,
                    comment: comment || undefined,
                  })
                }
              >
                {pending === 'rerun_chunk' ? <Spinner /> : null}
                Re-run chunk
              </Button>
              <Button
                variant="ghost"
                disabled={disabled}
                onClick={() =>
                  dispatch({
                    type: 'rerun_full',
                    payload: { full_run: true },
                    reviewer_id: reviewerId,
                    comment: comment || undefined,
                  })
                }
              >
                {pending === 'rerun_full' ? <Spinner /> : null}
                Re-run full run
              </Button>
            </div>

            {error && (
              <p role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}
            {hit.status !== 'open' && (
              <p className="text-xs text-muted italic">
                This hit is <strong>{hit.status}</strong>. Reopen in a follow-up ticket if needed.
              </p>
            )}
          </section>

          <section aria-labelledby="rm-audit">
            <h3 id="rm-audit" className="text-xs uppercase tracking-wide text-muted mb-2">
              Audit trail
            </h3>
            <AuditTrail entries={cachedAudit ?? []} loading={auditLoading} />
          </section>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const severityStyles = {
  low: 'bg-brand/15 text-brand-dark',
  medium: 'bg-warn/15 text-warn',
  high: 'bg-danger/15 text-danger',
} as const;

function formatCell(v: string | number | null | undefined): React.ReactNode {
  if (v === null || v === undefined || v === '') {
    return <span className="text-muted italic">—</span>;
  }
  return String(v);
}

interface DistRow {
  label: string;
  count: number;
  pct: number;
}

/**
 * Compute a tiny distribution of `original` values over the sample rows.
 * This is a proxy for the full column distribution, which the agent may
 * provide in a future payload revision; good enough to give the reviewer a
 * sense of what the column looks like.
 */
function computeDistribution(hit: HitlItem | null): DistRow[] {
  if (!hit || hit.sample_rows.length === 0) return [];
  const counts = new Map<string, number>();
  for (const r of hit.sample_rows) {
    const key = r.original === null || r.original === undefined ? '∅ null' : String(r.original);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const total = hit.sample_rows.length;
  return Array.from(counts.entries())
    .map(([label, count]) => ({ label, count, pct: Math.round((count / total) * 100) }))
    .sort((a, b) => b.count - a.count);
}
